import { Link } from 'react-router-dom';
import { useCountUp } from '../../../hooks/useCountUp';
import {
  getTopReadPosts,
  POST_READ_COUNTS,
  formatReadCount,
} from '../../../utils/blogUtils';

const RANK_CONFIG: Record<number, { bg: string; text: string; label: string }> = {
  0: { bg: 'bg-amber-400',  text: 'text-white', label: '🥇' },
  1: { bg: 'bg-gray-400',   text: 'text-white', label: '🥈' },
  2: { bg: 'bg-orange-700', text: 'text-white', label: '🥉' },
};

export default function MostReadPosts({ currentPostId }: { currentPostId: number }) {
  const ranked = getTopReadPosts(5, currentPostId);
  const maxCount = POST_READ_COUNTS[ranked[0]?.id] ?? 1;

  const targets = Object.fromEntries(ranked.map(p => [String(p.id), POST_READ_COUNTS[p.id] ?? 0]));
  const { values, containerRef, hasStarted } = useCountUp(targets);

  return (
    <div
      ref={containerRef}
      className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 dark:bg-orange-900/20">
          <i className="ri-fire-fill text-orange-500 text-base"></i>
        </div>
        <div className="flex-1">
          <p className="font-heading font-bold text-sm text-secondary dark:text-white leading-tight">
            Posts Mais Lidos
          </p>
          <p className="font-body text-xs text-gray-400 dark:text-gray-500">
            Conteúdo mais acessado
          </p>
        </div>
        <div className="flex items-center gap-1.5 bg-orange-50 dark:bg-orange-900/20 px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse"></span>
          <span className="font-body text-[10px] font-bold text-orange-500 uppercase tracking-wide">
            Ao vivo
          </span>
        </div>
      </div>

      {/* List */}
      <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
        {ranked.map((post, index) => {
          const target = POST_READ_COUNTS[post.id] ?? 0;
          const current = values[String(post.id)] ?? 0;
          const barWidth = Math.round((target / maxCount) * 100);
          const isTop = index === 0;
          const rankCfg = RANK_CONFIG[index];

          return (
            <li key={post.id}>
              <Link
                to={`/blog/${post.slug}`}
                className="flex items-start gap-3 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors cursor-pointer group"
              >
                <div className="flex-shrink-0 flex flex-col items-center gap-1 mt-0.5">
                  <span className={`w-6 h-6 flex items-center justify-center rounded-md text-xs font-heading font-bold ${rankCfg ? `${rankCfg.bg} ${rankCfg.text}` : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`}>
                    {rankCfg ? rankCfg.label : index + 1}
                  </span>
                </div>

                <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-body text-xs font-bold text-primary uppercase tracking-wide mb-0.5 truncate">
                    {post.category}
                  </p>
                  <p className="font-heading font-semibold text-sm text-secondary dark:text-white leading-snug line-clamp-2 group-hover:text-primary transition-colors mb-2">
                    {post.title}
                  </p>

                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`ri-eye-fill text-xs ${isTop ? 'text-orange-400' : 'text-gray-300 dark:text-gray-600'}`}></i>
                      </div>
                      <span className={`font-heading font-bold text-sm tabular-nums ${isTop ? 'text-orange-500' : 'text-gray-500 dark:text-gray-400'}`}>
                        {formatReadCount(current)}
                      </span>
                      <span className="font-body text-[10px] text-gray-400 dark:text-gray-500 ml-0.5">
                        leituras
                      </span>
                    </div>
                    {isTop && (
                      <span className="flex items-center gap-0.5 font-body text-[10px] font-bold text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full whitespace-nowrap">
                        <i className="ri-arrow-up-line text-xs"></i>
                        #1
                      </span>
                    )}
                  </div>

                  <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-[1600ms] ease-out ${isTop ? 'bg-orange-400' : 'bg-primary/40'}`}
                      style={{ width: hasStarted ? `${barWidth}%` : '0%' }}
                    />
                  </div>

                  <div className="flex items-center gap-1 mt-1.5">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-time-line text-xs text-gray-300 dark:text-gray-600"></i>
                    </div>
                    <span className="font-body text-[10px] text-gray-400 dark:text-gray-500">
                      {post.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/50">
        <Link
          to="/blog"
          className="flex items-center justify-center gap-1.5 w-full font-body text-xs font-semibold text-primary hover:gap-2.5 transition-all cursor-pointer whitespace-nowrap"
        >
          Ver todos os artigos
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
        </Link>
      </div>
    </div>
  );
}
